import { useEffect, useState } from 'react';
import type { PaymentMode } from '@dunea/shared';
import { apiGetAttribution, apiGetSearchConsole } from '~/lib/api/marketing';
import { classNames } from '~/utils/classNames';
import {
  CARD,
  CopyField,
  Field,
  LoadError,
  Section,
  Segmented,
  SkeletonCard,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import {
  ATTRIBUTION_DAYS,
  attributionTotals,
  buildUtmUrl,
  formatMinor,
  siteBaseUrl,
  UTM_EXAMPLE,
  UTM_PLACEHOLDER_URL,
} from './marketing-model';

type Days = (typeof ATTRIBUTION_DAYS)[number][0];

const TH = 'px-2 py-2 text-[10px] font-medium uppercase tracking-[0.12em] text-muted';

export function CampaignsView({ appId }: { appId: string }) {
  const [days, setDays] = useState<Days>('30');
  const [mode, setMode] = useState<PaymentMode>('live');
  const rows = useResource(() => apiGetAttribution(appId, Number(days), mode), `${appId}:${days}:${mode}`);
  // Only for the link builder's base URL; a failure just falls back to a placeholder.
  const site = useResource(() => apiGetSearchConsole(appId), appId);
  const baseUrl = siteBaseUrl(site.data);
  const totals = rows.data ? attributionTotals(rows.data) : null;

  const filters = (
    <div className="flex flex-wrap items-center gap-1.5">
      <Segmented label="Period" value={days} options={ATTRIBUTION_DAYS} onChange={setDays} />
      <Segmented
        label="Payment mode"
        value={mode}
        options={[
          ['test', 'Test'],
          ['live', 'Live'],
        ]}
        onChange={setMode}
      />
    </div>
  );

  return (
    <>
      <Section title="Revenue by campaign" icon="i-ph:chart-bar" aside={filters}>
        {rows.loading && !rows.data ? (
          <SkeletonCard lines={4} />
        ) : rows.error ? (
          <LoadError message="Could not load campaign revenue." onRetry={() => void rows.reload()} />
        ) : !rows.data?.length ? (
          <div className={classNames(CARD, 'space-y-1.5 px-4 py-5 text-center')}>
            <p className="text-[12px] text-secondary">No paid orders in this period yet.</p>
            <p className="text-[11px] leading-relaxed text-muted">
              Revenue is grouped by the UTM tags on the link a buyer arrived from. Tag every link you share in ads,
              posts and emails with the builder below, and orders show up here by source and campaign.
            </p>
          </div>
        ) : (
          <div className={classNames(CARD, 'overflow-x-auto')}>
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-hairline text-left">
                  <th className={classNames(TH, 'pl-3')}>Source</th>
                  <th className={TH}>Medium</th>
                  <th className={TH}>Campaign</th>
                  <th className={classNames(TH, 'text-right')}>Orders</th>
                  <th className={classNames(TH, 'pr-3 text-right')}>Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline">
                {rows.data.map((r) => (
                  <tr key={`${r.source}|${r.medium}|${r.campaign}|${r.currency}`}>
                    <td className="max-w-[9rem] truncate py-2.5 pl-3 pr-2 text-primary" title={r.source}>
                      {r.source}
                    </td>
                    <td className="max-w-[7rem] truncate px-2 py-2.5 text-secondary" title={r.medium}>
                      {r.medium}
                    </td>
                    <td className="max-w-[9rem] truncate px-2 py-2.5 text-secondary" title={r.campaign}>
                      {r.campaign}
                    </td>
                    <td className="px-2 py-2.5 text-right font-geist-mono text-secondary">{r.orders}</td>
                    <td className="py-2.5 pl-2 pr-3 text-right font-geist-mono text-primary">
                      {formatMinor(r.revenue, r.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totals && (
              <div className="flex items-start justify-between gap-3 border-t border-hairline px-3 py-2.5">
                <span className="text-[11px] text-muted">
                  {totals.orders === 1 ? '1 order' : `${totals.orders} orders`}, net of refunds
                </span>
                <div className="text-right">
                  {totals.revenue.map((t) => (
                    <div key={t.currency} className="font-geist-mono text-[13px] text-primary">
                      {formatMinor(t.amount, t.currency)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Section>

      <UtmBuilder baseUrl={baseUrl} />
    </>
  );
}

function UtmBuilder({ baseUrl }: { baseUrl: string | null }) {
  const [page, setPage] = useState(baseUrl ?? '');
  const [source, setSource] = useState<string>(UTM_EXAMPLE.source);
  const [medium, setMedium] = useState<string>(UTM_EXAMPLE.medium);
  const [campaign, setCampaign] = useState<string>(UTM_EXAMPLE.campaign);

  // The published address arrives after the first render; use it unless the owner typed their own.
  useEffect(() => {
    if (baseUrl) {
      setPage((p) => p || baseUrl);
    }
  }, [baseUrl]);

  const url = buildUtmUrl(page.trim() || UTM_PLACEHOLDER_URL, { source, medium, campaign });

  return (
    <Section title="Campaign link builder" icon="i-ph:link">
      <div className={classNames(CARD, 'space-y-3 p-4')}>
        <Field
          label="Page"
          hint={baseUrl ? undefined : 'Publish your site to use its real address. Any page of your site works.'}
        >
          <input
            value={page}
            onChange={(e) => setPage(e.target.value)}
            placeholder={UTM_PLACEHOLDER_URL}
            spellCheck={false}
            className="ui-input-mono"
          />
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="Source">
            <input
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="instagram"
              className="ui-input"
            />
          </Field>
          <Field label="Medium">
            <input
              value={medium}
              onChange={(e) => setMedium(e.target.value)}
              placeholder="social"
              className="ui-input"
            />
          </Field>
          <Field label="Campaign">
            <input
              value={campaign}
              onChange={(e) => setCampaign(e.target.value)}
              placeholder="diwali"
              className="ui-input"
            />
          </Field>
        </div>
        {url ? (
          <CopyField value={url} label="Your link" />
        ) : (
          <p className="text-[11px] text-danger/90">Enter a full address starting with https://</p>
        )}
        <p className="text-[11px] leading-relaxed text-muted">
          Source is where the link is posted (instagram, google, newsletter), medium is the kind of traffic (social,
          cpc, email), campaign is your name for the push.
        </p>
      </div>
    </Section>
  );
}
